import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import ToastService from '~/services/toast.service';

import { type UpdateInvoiceParsingInputPayload } from './types';
import { updateInvoiceParsingInput } from './updateInvoiceParsingInput';

/**
 * A custom hook to update the parsing input for a reprocessing invoice with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @param options - Optional configuration for the mutation
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/update_invoice_process_parsing_input_invoice_process__process_id__parsing_input_put
 */
export const useMutationUpdateInvoiceParsingInput = (
  options?: UseMutationOptions<void, unknown, UpdateInvoiceParsingInputPayload>,
) => {
  const { t } = useTranslation('invoiceReprocessing');

  return useMutation({
    mutationFn: async ({ processId, parsingInput }) =>
      updateInvoiceParsingInput({
        processId,
        parsingInput,
      }),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSuccess() {
      ToastService.success(
        t(($) => $.processEdit.mutation.updateInvoiceParsingInput.success),
      );
    },
    ...options,
  });
};
